import { Link } from "react-router-dom";
import { Trash2, Mail, Globe, CheckSquare, Square, CheckCircle2, XCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function LeadTable({ leads, pipeline, selectedIds, onToggleSelect, onToggleAll, onRefresh }) {
  
  const updateStage = async (id, stageId) => {
    try {
      await api.patch(`/crm/leads/${id}/stage`, { pipeline_stage: stageId });
      toast.success("Stage updated");
      onRefresh && onRefresh();
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this prospect?")) return;
    await api.delete(`/leads/${id}`);
    onRefresh && onRefresh();
  };

  if (!leads || leads.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#EAEAE3] rounded-2xl p-16 text-center text-[#5C5C66]">
        No prospects to show.
      </div>
    );
  }

  const allSelected = selectedIds.size === leads.length && leads.length > 0;
  
  const getStageConfig = (stageId) => {
    if (!pipeline?.stages) return null;
    return pipeline.stages.find(s => s.id === stageId) || pipeline.stages.find(s => s.id === "s_new");
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl overflow-hidden shadow-soft">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAF7] border-b border-[#EAEAE3]">
            <tr className="text-left">
              <th className="px-4 py-3 w-10">
                <button onClick={onToggleAll} className="text-[#9A9AA3] hover:text-[#002FA7]">
                  {allSelected ? <CheckSquare className="h-4 w-4 text-[#002FA7]" /> : <Square className="h-4 w-4" />}
                </button>
              </th>
              <Th>Prospect</Th>
              <Th>Company</Th>
              <Th>Contact</Th>
              <Th>Score / Deal</Th>
              <Th>Pipeline Stage</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const isSelected = selectedIds.has(l.id);
              const stage = getStageConfig(l.pipeline_stage);
              
              return (
                <tr key={l.id} className={`border-b border-[#EAEAE3] last:border-0 transition-colors ${isSelected ? 'bg-[#002FA7]/5' : 'hover:bg-[#FAFAF7]'}`} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => onToggleSelect(l.id)} className={`${isSelected ? 'text-[#002FA7]' : 'text-[#9A9AA3] hover:text-[#002FA7]'}`}>
                      {isSelected ? <CheckSquare className="h-4 w-4 text-[#002FA7]" /> : <Square className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/leads/${l.id}`} className="flex items-center gap-3 hover:text-[#0A0A0F]">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#002FA7] to-[#1E4FD6] text-white flex items-center justify-center text-[10px] font-bold shrink-0 shadow-inner">
                        {l.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[150px]">{l.name}</div>
                        <div className="text-xs text-[#5C5C66] truncate max-w-[150px]">{l.title || "?"}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium truncate max-w-[150px]">{l.company || "?"}</div>
                    <div className="text-xs text-[#5C5C66] truncate max-w-[150px]">{l.location || ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 text-xs">
                      {l.email && (
                        <span className="inline-flex items-center gap-1.5 text-[#0A0A0F]">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[180px]">{l.email}</span>
                          {l.verification_status === "valid" && (
                            <span title="Deliverable — verified via SMTP" className="shrink-0"><CheckCircle2 className="h-3 w-3 text-green-500" /></span>
                          )}
                          {l.verification_status === "invalid" && (
                            <span title="Undeliverable — mailbox not found" className="shrink-0"><XCircle className="h-3 w-3 text-red-500" /></span>
                          )}
                          {l.verification_status === "catch_all" && (
                            <span title="Catch-all domain — delivery uncertain" className="shrink-0"><AlertTriangle className="h-3 w-3 text-amber-500" /></span>
                          )}
                          {l.verification_status === "unknown" && (
                            <span title="Unverified — SMTP check was inconclusive" className="shrink-0"><ShieldCheck className="h-3 w-3 text-slate-400" /></span>
                          )}
                          {(!l.verification_status || l.verification_status === "unverified") && (
                            <span title="Not yet verified" className="w-2 h-2 rounded-full bg-slate-200 border border-slate-300 shrink-0" />
                          )}
                        </span>
                      )}
                      {l.website && <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#5C5C66] hover:text-[#002FA7] truncate max-w-[200px]"><Globe className="h-3 w-3" />{l.website}</a>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono font-bold text-[#002FA7] text-base">{l.score}</div>
                    {l.deal_value > 0 && (
                      <div className="text-xs font-mono text-[#5C5C66] mt-0.5">{formatCurrency(l.deal_value)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={l.pipeline_stage || "s_new"} onValueChange={(v) => updateStage(l.id, v)}>
                      <SelectTrigger className="h-8 rounded-lg w-40 text-xs bg-white border-[#EAEAE3]">
                        {stage ? (
                          <div className="flex items-center gap-2 truncate">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />
                            <span className="truncate">{stage.name}</span>
                          </div>
                        ) : <SelectValue />}
                      </SelectTrigger>
                      <SelectContent>
                        {pipeline?.stages?.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                              {s.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(l.id)} className="text-[#9A9AA3] hover:text-red-500 transition-colors" data-testid={`delete-lead-${l.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 label-mono font-mono whitespace-nowrap">{children}</th>;
}
